<template>
  <div class="confirm-step">
    <p class="confirm-step__summary">{{ summaryText }}</p>

    <ul v-if="fileErrors.length > 0" class="confirm-step__warning">
      <li v-for="(error, index) in fileErrors" :key="index">{{ error }}</li>
    </ul>

    <!-- Above the table: a confirm failure must be visible without scrolling past
         a long preview. -->
    <div class="confirm-step__error" role="alert">
      {{ store.errorMessage }}
    </div>

    <PreviewTable
      :rows="previewRows"
      :outcome-labels="store.outcomeLabels"
      :status-labels="store.statusLabels"
      :labels="store.labels"
      :client-control-enabled="store.clientControlEnabled"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import PreviewTable from "./PreviewTable.ce.vue";
import { useAssignSpecialtiesStore } from "#ce/apps/SpAdmin/SpAssignSpecialtiesApp/store/assign-specialties";
import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";

const store = useAssignSpecialtiesStore();

const summary = computed(() => ({
  new: 0,
  existing: 0,
  will_enable: 0,
  errors: 0,
  total: 0,
  ...(store.previewData?.summary ?? {}),
}));
const previewRows = computed(() => store.previewData?.rows ?? []);
const fileErrors = computed(() => store.previewData?.errors ?? []);

// Compose the summary sentence from server-provided templates so the wording
// localizes and adapts per deployment (e.g. non-canonical "will be added" vs
// canonical "will be requested"). The will-enable fragment only applies on
// deployments with client control; the errors fragment only when errors exist.
const summaryText = computed(() => {
  const templates = store.labels.summary;
  const parts = [
    pluralizeTemplate(templates.new, summary.value.new),
    pluralizeTemplate(templates.existing, summary.value.existing),
  ];

  if (store.clientControlEnabled && summary.value.will_enable > 0) {
    parts.push(pluralizeTemplate(templates.willEnable, summary.value.will_enable));
  }

  if (summary.value.errors > 0) {
    parts.push(pluralizeTemplate(templates.errors, summary.value.errors));
  }

  return parts.join(" ");
});
</script>

<style scoped lang="scss">
.confirm-step {
  display: flex;
  flex-direction: column;
  gap: var(--sp-ref-scale-16);

  &__summary {
    font-size: var(--sp-sys-typescale-body-font-size, 1rem);
    margin: 0;
  }

  &__warning {
    // These are file-level errors: use the error token (the warning token's
    // yellow fails contrast on the light surface).
    color: var(--sp-sys-color-error, #d32f2f);
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    margin: 0;
  }

  &__error {
    color: var(--sp-sys-color-error, #d32f2f);
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);
    min-height: 1.25rem;
  }
}
</style>
